import { backendClient } from '../lib/api/backendClient';
import { composerService } from '../services/composers';
import { getFromLocalStorageWithExpiry, setLocalStorageWithExpiry } from './localstorage';

const knownComposerIds = new Set<string>();
const spotifyToCatalogId = new Map<string, string>();
const pendingLookups = new Map<string, Promise<boolean>>();
let catalogFetch: Promise<Set<string>> | null = null;
let catalogIds = new Set<string>();
const composerImageMap = new Map<string, string>();
const composerNameMap = new Map<string, string>();

const COMPOSER_CACHE_KEY = 'cmf.catalog-composers.v1';
const COMPOSER_CACHE_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours

type ComposerCacheEntry = {
  spotifyId: string;
  catalogId?: string;
  imageUrl?: string;
};

type ComposerCachePayload = {
  version: 1;
  entries: ComposerCacheEntry[];
};

const normalizeSpotifyArtistId = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    if (trimmed.startsWith('spotify:artist:')) {
      const parts = trimmed.split(':');
      const id = parts.pop();
      return id ?? undefined;
    }
    if (!trimmed.includes(':') && !trimmed.includes('/') && trimmed.length >= 10) {
      return trimmed;
    }
  }
  return undefined;
};

export const extractSpotifyArtistIdFromComposer = (composer: Record<string, any>): string | undefined => {
  const direct =
    composer.spotifyArtistId ??
    composer.spotify_artist_id ??
    composer.spotifyId ??
    composer.spotify_id ??
    composer.artistSpotifyId ??
    composer.artist_spotify_id ??
    composer.spotifyArtist ??
    composer.spotify_artist;

  let spotifyId = normalizeSpotifyArtistId(direct);
  if (spotifyId) return spotifyId;

  spotifyId = normalizeSpotifyArtistId(composer.uri);
  if (spotifyId) return spotifyId;

  return normalizeSpotifyArtistId(composer.id);
};

const normalizeCatalogId = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const normalizeComposerName = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const extractComposerName = (composer: Record<string, any>): string | undefined => {
  return (
    normalizeComposerName(composer?.name) ??
    normalizeComposerName(composer?.displayName) ??
    normalizeComposerName(composer?.display_name) ??
    normalizeComposerName(composer?.fullName) ??
    normalizeComposerName(composer?.full_name) ??
    normalizeComposerName(composer?.sortableName) ??
    normalizeComposerName(composer?.sortable_name) ??
    normalizeComposerName(composer?.composerName) ??
    normalizeComposerName(composer?.composer_name) ??
    normalizeComposerName(composer?.primaryName) ??
    normalizeComposerName(composer?.primary_name) ??
    normalizeComposerName(composer?.displayLabel) ??
    normalizeComposerName(composer?.display_label) ??
    normalizeComposerName(composer?.title)
  );
};

const addComposerId = (
  spotifyArtistId: string | null | undefined,
  catalogComposerId?: string | null | undefined
) => {
  const normalizedSpotify = normalizeSpotifyArtistId(spotifyArtistId);
  if (!normalizedSpotify) return;
  knownComposerIds.add(normalizedSpotify);
  catalogIds.add(normalizedSpotify);

  const normalizedCatalog = normalizeCatalogId(catalogComposerId);
  if (normalizedCatalog) {
    spotifyToCatalogId.set(normalizedSpotify, normalizedCatalog);
  }
};

export const registerComposerName = (composerId: string, name?: string) => {
  const normalizedId = normalizeCatalogId(composerId);
  const normalizedName = normalizeComposerName(name);
  if (!normalizedId || !normalizedName) return;
  const existing = composerNameMap.get(normalizedId);
  if (existing === normalizedName) return;
  composerNameMap.set(normalizedId, normalizedName);
};

export const getComposerName = (composerId: string): string | undefined => {
  const normalized = normalizeCatalogId(composerId);
  if (!normalized) return undefined;
  return composerNameMap.get(normalized);
};

export const registerKnownComposerId = (
  spotifyArtistId: string | null | undefined,
  catalogComposerId?: string | null | undefined
) => {
  addComposerId(spotifyArtistId ?? undefined, catalogComposerId ?? undefined);
};

const extractCatalogComposerId = (composer: Record<string, any>): string | undefined => {
  const candidate = normalizeCatalogId(composer?.id);
  if (candidate) return candidate;

  const legacyCandidate = normalizeCatalogId(
    composer?.composerId ?? composer?.composer_id ?? (composer as any)?.catalogId
  );
  if (legacyCandidate) return legacyCandidate;

  return undefined;
};

const extractImageUrlFromComposer = (composer: Record<string, any>): string | undefined => {
  const candidates = [
    composer.imageUrl,
    composer.image_url,
    composer.coverUrl,
    composer.cover_url,
    composer.primaryImage?.url,
    composer.primary_image?.url
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  if (Array.isArray(composer.images)) {
    for (const image of composer.images) {
      if (image && typeof image.url === 'string' && image.url.trim()) {
        return image.url.trim();
      }
    }
  }

  return undefined;
};

const registerComposerImageMetadata = (composer: Record<string, any>) => {
  const imageUrl = extractImageUrlFromComposer(composer);
  if (!imageUrl) {
    return;
  }

  const targets = new Set<string>();
  const catalogId = extractCatalogComposerId(composer);
  if (catalogId) targets.add(catalogId);
  const explicitId = normalizeCatalogId(composer?.id);
  if (explicitId) targets.add(explicitId);
  const spotifyId = extractSpotifyArtistIdFromComposer(composer);
  if (spotifyId) targets.add(spotifyId);

  targets.forEach((key) => {
    composerImageMap.set(key, imageUrl);
  });
};

export const registerKnownComposerEntities = (composers: Record<string, any>[] | null | undefined) => {
  if (!Array.isArray(composers)) return;
  composers.forEach((composer) => {
    registerComposerImageMetadata(composer);
    const catalogId = extractCatalogComposerId(composer);
    if (catalogId) {
      const composerName = extractComposerName(composer);
      registerComposerName(catalogId, composerName);
    }
    const spotifyId = extractSpotifyArtistIdFromComposer(composer);
    if (!spotifyId) return;
    addComposerId(spotifyId, catalogId);
  });
};

export const isComposerIdKnown = (spotifyArtistId: string | null | undefined): boolean => {
  if (!spotifyArtistId) return false;
  return knownComposerIds.has(spotifyArtistId);
};

export const ensureComposerKnown = async (spotifyArtistId: string | null | undefined): Promise<boolean> => {
  if (!spotifyArtistId) return false;

  if (knownComposerIds.has(spotifyArtistId)) {
    return true;
  }

  const existing = pendingLookups.get(spotifyArtistId);
  if (existing) {
    return existing;
  }

  const lookup = composerService
    .fetchBySpotifyId(spotifyArtistId)
    .then((composer) => {
      if (composer) {
        registerKnownComposerEntities([composer as Record<string, any>]);
        return true;
      }

      return false;
    })
    .catch(() => false)
    .finally(() => {
      pendingLookups.delete(spotifyArtistId);
    });

  pendingLookups.set(spotifyArtistId, lookup);
  return lookup;
};

export const getKnownComposerIds = (): ReadonlySet<string> => knownComposerIds;

export const getCatalogIdForSpotifyId = (
  spotifyArtistId: string | null | undefined
): string | undefined => {
  const normalized = normalizeSpotifyArtistId(spotifyArtistId);
  if (!normalized) return undefined;
  return spotifyToCatalogId.get(normalized);
};

export const registerComposerMapping = (
  spotifyArtistId: string | null | undefined,
  catalogComposerId: string | null | undefined
) => {
  addComposerId(spotifyArtistId ?? undefined, catalogComposerId ?? undefined);
};

export const registerComposerMetadata = (composer: Record<string, any> | null | undefined) => {
  if (!composer) return;
  registerComposerImageMetadata(composer);
  const catalogId = extractCatalogComposerId(composer);
  if (catalogId) {
    const composerName = extractComposerName(composer);
    registerComposerName(catalogId, composerName);
  }
};

export const getComposerImageMapSnapshot = (): ReadonlyMap<string, string> => new Map(composerImageMap);

const persistCatalogComposers = (composers: Record<string, any>[]) => {
  try {
    const seen = new Set<string>();
    const entries: ComposerCacheEntry[] = [];

    composers.forEach((composer) => {
      const spotifyId = extractSpotifyArtistIdFromComposer(composer);
      if (!spotifyId || seen.has(spotifyId)) return;
      seen.add(spotifyId);

      const catalogId = extractCatalogComposerId(composer);
      const imageUrl = extractImageUrlFromComposer(composer);
      entries.push({ spotifyId, catalogId, imageUrl });
    });

    if (entries.length === 0) return;

    setLocalStorageWithExpiry(
      COMPOSER_CACHE_KEY,
      {
        version: 1,
        entries
      } satisfies ComposerCachePayload,
      COMPOSER_CACHE_TTL_MS
    );
  } catch {
    // Best-effort cache; ignore storage failures.
  }
};

const hydrateCatalogFromCache = (): boolean => {
  try {
    const cached = getFromLocalStorageWithExpiry(COMPOSER_CACHE_KEY) as ComposerCachePayload | null;
    if (!cached || cached.version !== 1 || !Array.isArray(cached.entries) || cached.entries.length === 0) {
      return false;
    }

    cached.entries.forEach((entry) => {
      addComposerId(entry.spotifyId, entry.catalogId);
      const normalizedImage =
        typeof entry.imageUrl === 'string' && entry.imageUrl.trim() ? entry.imageUrl.trim() : undefined;
      if (!normalizedImage) return;

      const normalizedSpotify = normalizeSpotifyArtistId(entry.spotifyId);
      const normalizedCatalog = normalizeCatalogId(entry.catalogId);
      if (normalizedSpotify) composerImageMap.set(normalizedSpotify, normalizedImage);
      if (normalizedCatalog) composerImageMap.set(normalizedCatalog, normalizedImage);
    });

    if (process.env.NODE_ENV === 'development') {
      console.info('[composerRegistry] restored catalog composers from cache', {
        size: catalogIds.size
      });
    }

    return catalogIds.size > 0;
  } catch {
    return false;
  }
};

export const loadCatalogComposerIds = async (): Promise<Set<string>> => {
  if (catalogIds.size) {
    return new Set(catalogIds);
  }

  if (hydrateCatalogFromCache() && catalogIds.size) {
    return new Set(catalogIds);
  }

  if (catalogFetch) {
    return catalogFetch;
  }

  const start = process.env.NODE_ENV === 'development' ? Date.now() : 0;
  if (process.env.NODE_ENV === 'development') {
    console.info('[composerRegistry] loading catalog composers');
  }

  catalogFetch = backendClient
    .get<{ composers?: any[] }>('/catalog/composers')
    .then((response) => {
      const list = Array.isArray(response.data?.composers) ? response.data!.composers! : [];
      registerKnownComposerEntities(list);
      persistCatalogComposers(list);
      return new Set(catalogIds);
    })
    .catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[composerRegistry] Failed to load catalog composers', error);
      }
      return new Set<string>(catalogIds);
    })
    .finally(() => {
      if (process.env.NODE_ENV === 'development') {
        console.info('[composerRegistry] load completed', {
          durationMs: Date.now() - start,
          size: catalogIds.size
        });
      }
      catalogFetch = null;
    });

  const result = await catalogFetch;
  return new Set(result);
};
